/**
 * usePdfLibrary — V7.2.B (2026-06-03).
 *
 * Fetches the PDF manifest from the briefing-room backend + exposes a
 * 3-state union (idle/loading/ready/error) per MEMORY.md rule
 * `feedback_async_then_render_needs_loading_state`:
 *
 *   "Anti-pattern to avoid: boolean isLoading that doubles as isActive."
 *
 * Precedent: useTtsQueue.ts:35
 *   export type TtsQueueState = 'idle' | 'loading' | 'playing' | 'cancelled';
 *
 * The hook accepts an injected PdfServiceModule so tests can stub the
 * underlying fetch via constructor injection (mirrors PersistenceServiceModule
 * pattern used across the host services suite).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PdfMeta } from '../services/host/PdfServiceModule';
import { PdfServiceModule } from '../services/host/PdfServiceModule';

export type PdfLibraryState = 'idle' | 'loading' | 'ready' | 'error';

export interface PdfLibraryHookResult {
  state: PdfLibraryState;
  pdfs: PdfMeta[];
  error: string | null;
  refresh: () => void;
}

export function usePdfLibrary(
  pdfService?: PdfServiceModule,
): PdfLibraryHookResult {
  // Stabilize the default instance with useMemo so useEffect's
  // [resolvedService] dep doesn't change every render. Without this,
  // omitting the pdfService arg would create a new instance per
  // render → infinite fetch loop.
  const resolvedService = useMemo(
    () => pdfService ?? new PdfServiceModule(),
    [pdfService],
  );

  const [state, setState] = useState<PdfLibraryState>('idle');
  const [pdfs, setPdfs] = useState<PdfMeta[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    // setState('loading') BEFORE the fetch call — the drawer must show
    // a loading affordance during the round-trip (V4.7 useTtsQueue
    // precedent at lines 93-96). Without this, the drawer would show
    // empty list during fetch and appear broken.
    setState('loading');
    setError(null);

    (async () => {
      try {
        const result = await resolvedService.listPdfs();
        if (cancelled) return;
        setPdfs(result);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setPdfs([]);
        setError(e instanceof Error ? e.message : String(e));
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, refreshTick]);

  return { state, pdfs, error, refresh };
}
